import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home, Mail, CreditCard, Newspaper, Calendar, Menu, LogOut,
  Users, Activity, Ticket, Target, UserPlus, BarChart3, ListTodo,
  MessageSquare, LayoutDashboard, Search, Shield, TrendingUp,
  ChevronDown, ChevronRight, FileText, KeyRound, Megaphone, Webhook,
  Sparkles,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { logoutUser } from "../../store/slices/user.slice";

const navSections = [
  {
    label: "Overview",
    items: [
      { name: "Dashboard", path: "/admin/dashboard", icon: Home },
      { name: "Business Insights", path: "/admin/business-insights", icon: TrendingUp },
      { name: "Analytics", path: "/admin/analytics", icon: BarChart3 },
      { name: "Demo Tours", path: "/admin/demos", icon: Sparkles },
      { name: "Activities", path: "/admin/activities", icon: Activity },
    ],
  },
  {
    label: "Users & Accounts",
    items: [
      { name: "All Users", path: "/admin/user-management", icon: Users },
      { name: "Demo Follow-Ups", path: "/admin/follow-ups", icon: UserPlus },
      { name: "Account Look Up", path: "/admin/account-look-up", icon: Search },
      { name: "Leads", path: "/admin/leads", icon: UserPlus },
      { name: "Affiliates", path: "/admin/affiliates", icon: Users },
    ],
  },
  {
    label: "Revenue",
    items: [
      { name: "Payments", path: "/admin/payments", icon: CreditCard },
      { name: "Goals", path: "/admin/goals", icon: Target },
    ],
  },
  {
    label: "Communications",
    items: [
      { name: "Emails", path: "/admin/emails", icon: Mail },
      { name: "Broadcast", path: "/admin/broadcast-email", icon: Megaphone },
      { name: "Texting", path: "/admin/texting", icon: MessageSquare },
    ],
  },
  {
    label: "Operations",
    items: [
      { name: "Tasks & Notes", path: "/admin/tasks", icon: ListTodo },
      { name: "Tickets", path: "/admin/tickets", icon: Ticket },
      { name: "List Formatting", path: "/admin/list-formatting", icon: FileText },
      { name: "Calendar", path: "/admin/calendar", icon: Calendar },
      { name: "Newsletter", path: "/admin/news-letters", icon: Newspaper },
    ],
  },
  {
    label: "Security",
    items: [
      { name: "My Security (2FA)", path: "/admin/security", icon: KeyRound },
      { name: "Suspicious Activity", path: "/admin/suspicious-activity", icon: Shield },
      { name: "Audit Log", path: "/admin/audit-log", icon: FileText },
      { name: "Webhook Events", path: "/admin/webhook-events", icon: Webhook },
    ],
  },
];

const SideBar = ({ collapsed, setCollapsed }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState(
    navSections.reduce((acc, s) => ({ ...acc, [s.label]: true }), {})
  );
  const [pendingFormattingCount, setPendingFormattingCount] = useState(0);
  const [notifCounts, setNotifCounts] = useState({
    openTickets: 0,
    pendingAffiliates: 0,
    lockedAccounts: 0,
    criticalEvents24h: 0,
  });

  // Fetch pending list formatting count for badge
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem("__admin_token__") || localStorage.getItem("__login_user_token__") || localStorage.getItem("token");
        if (!token) return;
        const apiBase = import.meta.env.VITE_API_URL || 'https://stacked-backend-gv43.onrender.com/api/v1';
        const res = await fetch(`${apiBase}/list-formatting/admin/pending-count`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (json.success) setPendingFormattingCount(json.data?.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    // Refresh every 60 seconds
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch unified admin notification counts (tickets, affiliates, security)
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const token = localStorage.getItem("__admin_token__") || localStorage.getItem("__login_user_token__") || localStorage.getItem("token");
        if (!token) return;
        const apiBase = import.meta.env.VITE_API_URL || 'https://stacked-backend-gv43.onrender.com/api/v1';
        const res = await fetch(`${apiBase}/admin/notification-counts`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (json.success) setNotifCounts(json.data || notifCounts);
      } catch { /* ignore */ }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map each sidebar path to its badge count
  const badgeFor = (path) => {
    if (path === "/admin/tickets") return notifCounts.openTickets;
    if (path === "/admin/affiliates") return notifCounts.pendingAffiliates;
    if (path === "/admin/suspicious-activity") return notifCounts.lockedAccounts + notifCounts.criticalEvents24h;
    if (path === "/admin/list-formatting") return pendingFormattingCount;
    return 0;
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    // Full reload kills the Redux store + every RTK Query cache so
    // the next sign-in starts clean. SPA navigate would leave cached
    // admin data behind.
    window.location.href = "/login";
  };

  const toggleSection = (label) => {
    if (collapsed) return;
    setExpandedSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div
      className={`${
        collapsed ? "w-[70px]" : "w-[250px]"
      } fixed h-full transition-all duration-300 bg-[var(--dark-bg)] border-r border-[var(--primary-color)]/30 flex flex-col z-50`}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-[var(--primary-color)]/30 flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[var(--primary-color)]" />
            <span className="text-white font-bold text-base tracking-wide">
              STACKED <span className="text-[var(--primary-color)]">Admin</span>
            </span>
          </div>
        )}
        <button
          onClick={() => setCollapsed((prev) => !prev)}
          className="text-gray-400 hover:text-[var(--primary-color)] hover:bg-[var(--lighter-dark)] p-2 rounded-lg transition-colors"
        >
          <Menu size={18} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {navSections.map((section) => (
          <div key={section.label} className="mb-1">
            {!collapsed && (
              <button
                onClick={() => toggleSection(section.label)}
                className="flex items-center justify-between w-full px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-gray-500 hover:text-gray-300 transition-colors"
              >
                <span>{section.label}</span>
                {expandedSections[section.label] ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
            )}

            {(collapsed || expandedSections[section.label]) && (
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const badge = badgeFor(item.path);
                  return (
                    <li key={item.name}>
                      <NavLink
                        to={item.path}
                        title={collapsed ? item.name : undefined}
                        className={({ isActive }) =>
                          `flex items-center gap-3 w-full px-3 py-2 text-[13px] rounded-lg transition-all ${
                            isActive
                              ? "bg-[var(--lighter-dark)] text-[var(--primary-color)] font-medium border-r-2 border-[var(--primary-color)]"
                              : "text-gray-400 hover:bg-[var(--lighter-dark)] hover:text-white"
                          } ${collapsed ? "justify-center" : ""}`
                        }
                      >
                        <div className="relative flex-shrink-0">
                          <Icon className="w-[18px] h-[18px]" />
                          {badge > 0 && collapsed && (
                            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                              {badge > 9 ? "9+" : badge}
                            </span>
                          )}
                        </div>
                        {!collapsed && (
                          <>
                            <span className="flex-1">{item.name}</span>
                            {badge > 0 && (
                              <span className="ml-auto px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] text-center">
                                {badge}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-[var(--primary-color)]/30 p-2 flex-shrink-0 space-y-0.5">
        <NavLink
          to="/realestate/dashboard"
          className="flex items-center gap-3 w-full px-3 py-2 text-[13px] rounded-lg transition-all text-gray-400 hover:bg-[var(--lighter-dark)] hover:text-[var(--primary-color)]"
        >
          <LayoutDashboard className="w-[18px] h-[18px] flex-shrink-0" />
          {!collapsed && <span>Go to CRM</span>}
        </NavLink>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2 text-[13px] rounded-lg transition-all text-gray-400 hover:bg-red-500/10 hover:text-red-400"
        >
          <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default SideBar;
